import React, { useState, useEffect } from 'react';
import { Box, Heading, VStack, Text, Select, Spinner, FormControl, FormLabel, Textarea, Button, HStack, useToast, Input, Card, CardBody, SimpleGrid, Flex, Spacer, IconButton, Icon, Tabs, TabList, TabPanels, TabPanel, Tab } from '@chakra-ui/react';
import PageLayout from '../components/PageLayout';
import { FiFileText, FiUploadCloud, FiDownload, FiTrash2 } from 'react-icons/fi';
import { Building2 } from 'lucide-react';
import { getClubs } from '../services/api';

// Assuming you have a way to fetch clubs with document counts
// import { fetchClubsWithDocCountsApi } from '../api';
// Assuming you have API functions for KB management
// import { fetchKnowledgeBaseApi, addKnowledgeEntryApi, uploadKnowledgeFileApi, exportKnowledgeBaseApi, importKnowledgeBaseApi } from '../api';

const DocumentsManagement = () => {
  const [clubs, setClubs] = useState([]);
  const [selectedClubId, setSelectedClubId] = useState(null);
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [clubDocuments, setClubDocuments] = useState([]);
  const [allClubDocs, setAllClubDocs] = useState({}); // { clubId: [docs] }
  const [loadingClubs, setLoadingClubs] = useState(true);
  const [loadingKB, setLoadingKB] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [newEntry, setNewEntry] = useState('');
  const [search, setSearch] = useState('');
  const toast = useToast();
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadUrls, setUploadUrls] = useState(['']);
  const [uploadNotes, setUploadNotes] = useState('');

  // Fetch clubs on component mount
  useEffect(() => {
    fetchClubsList();
  }, []);

  // Fetch club documents when a club is selected
  useEffect(() => {
    if (selectedClubId) {
      fetchKnowledgeBase(selectedClubId);
      fetchClubDocuments(selectedClubId);
      // Reset upload form when new club is selected
      setUploadFiles([]);
      setUploadUrls(['']);
      setUploadNotes('');
      setNewEntry(''); // Also reset new KB entry
    } else {
      setKnowledgeBase([]);
      setClubDocuments([]);
      setUploadFiles([]);
      setUploadUrls(['']);
      setUploadNotes('');
      setNewEntry('');
    }
  }, [selectedClubId]);

  // Fetch real clubs from backend
  const fetchClubsList = async () => {
    setLoadingClubs(true);
    try {
      const response = await getClubs();
      // response.data is an array of clubs
      const clubsData = response.data.map(club => ({
        id: club._id,
        name: club.name,
        documentCount: 0 // You can update this if you want to count docs per club
      }));
      setClubs(clubsData);
      // Fetch document counts for all clubs
      const docsByClub = {};
      for (const club of clubsData) {
        const res = await fetch(`/api/documents/${club.id}/documents`);
        if (res.ok) {
          const docs = await res.json();
          docsByClub[club.id] = docs;
        } else {
          docsByClub[club.id] = [];
        }
      }
      setAllClubDocs(docsByClub);
      setClubs(clubsData.map(club => ({ ...club, documentCount: docsByClub[club.id]?.length || 0 })));
    } catch (error) {
      toast({
        title: 'Error fetching clubs.',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setClubs([]);
    } finally {
      setLoadingClubs(false);
    }
  };

  // Fetch club documents from backend
  const fetchClubDocuments = async (clubId) => {
    setLoadingDocs(true);
    try {
      // Replace with real API call in production
      const res = await fetch(`/api/documents/${clubId}/documents`);
      if (!res.ok) throw new Error('Failed to fetch documents');
      const docs = await res.json();
      setClubDocuments(docs);
    } catch (error) {
      toast({
        title: 'Error fetching documents.',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setClubDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  // Fetch knowledge base when a club is selected
  useEffect(() => {
    if (selectedClubId) {
      fetchKnowledgeBase(selectedClubId);
    } else {
      setKnowledgeBase([]); // Clear KB when no club is selected
    }
  }, [selectedClubId]);

  const fetchKnowledgeBase = async (clubId) => {
    setLoadingKB(true);
    try {
      // Simulate fetching knowledge base for the selected club
      console.log(`Fetching knowledge base for users ID: ${clubId}`);
      const dummyKB = [
        { id: 'kb-a1', type: 'text', text: `Sample text entry for club ${clubId}`, source: 'manual' },
        { id: 'kb-b2', type: 'file', fileName: `sample_doc_${clubId}.pdf`, source: 'upload' },
      ];
      await new Promise(resolve => setTimeout(resolve, 700));
      setKnowledgeBase(dummyKB);
    } catch (error) {
       toast({
        title: 'Error fetching knowledge base.',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingKB(false);
    }
  };

  const handleAddEntry = () => {
    if (!selectedClubId) {
      toast({
        title: 'Please select a users first.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (newEntry.trim() === '') {
      toast({
        title: 'Cannot add empty entry.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    // Add new entry to knowledgeBase
    const entryToAdd = { id: `kb-${selectedClubId}-${Date.now()}`, type: 'text', text: newEntry.trim(), source: 'manual' };
    setKnowledgeBase(prev => [...prev, entryToAdd]);
    setNewEntry('');
    toast({
      title: 'Entry added.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleFileUpload = (event) => {
     if (!selectedClubId) {
      toast({
        title: 'Please select a users first.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // Simulate uploading file and getting a reference/filename back
        const newFileEntry = {
          id: `kb-${selectedClubId}-${Date.now()}-file`, // Unique ID
          type: 'file',
          fileName: file.name,
          source: 'upload' // Indicate source is a file upload
        };
        setKnowledgeBase(prev => [...prev, newFileEntry]); // Optimistic update
        toast({
          title: 'File uploaded and added to knowledge base.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        // In a real app, you'd upload the file to storage and save the URL/path via API
      } catch (error) {
        toast({
          title: 'Error processing file.',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };
     // Reading as text for simplicity - adjust based on expected file types
    reader.readAsText(file);
  };

   const handleExport = () => {
     if (!selectedClubId) {
      toast({
        title: 'Please select a users first.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    // Simulate fetching latest KB for export (optional, could export current state)
     // In a real app, you might call an API to generate and download the export file
    const dataToExport = JSON.stringify(knowledgeBase, null, 2);
    const blob = new Blob([dataToExport], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `knowledge_base_${selectedClubId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({
      title: 'Knowledge base exported.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleImport = (event) => {
    if (!selectedClubId) {
      toast({
        title: 'Please select a users first.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        // Basic validation (you might want more robust validation)
        if (Array.isArray(importedData) && importedData.every(item => item.id && item.type && (item.type === 'text' && item.text || item.type === 'file' && item.fileName))) {
          // In a real application, you would send this data to your backend to replace/merge
          // For now, replacing current KB with imported data
          setKnowledgeBase(importedData);
          toast({
            title: 'Knowledge base imported.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        } else {
          throw new Error('Invalid file format or content.');
        }
      } catch (error) {
        toast({
          title: 'Error importing file.',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };
    reader.readAsText(file);
  };

  const handleExportClubDocs = (clubId) => {
    const docs = allClubDocs[clubId] || [];
    const dataToExport = JSON.stringify(docs, null, 2);
    const blob = new Blob([dataToExport], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `club_${clubId}_documents.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({
      title: 'Documents exported.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleClubSelect = (clubId) => {
    setSelectedClubId(clubId);
  };

  // Helper function to get the selected club object
  const getSelectedClub = () => {
    return clubs.find(club => club.id === selectedClubId);
  };

  const selectedClub = getSelectedClub();

  // Filter clubs by search
  const filteredClubs = clubs.filter(club => club.name.toLowerCase().includes(search.toLowerCase()));

  const handleUploadFileChange = (e) => setUploadFiles(e.target.files);
  const handleUploadUrlChange = (i, value) => {
    const newUrls = [...uploadUrls];
    newUrls[i] = value;
    setUploadUrls(newUrls);
  };
  const addUploadUrlField = () => setUploadUrls([...uploadUrls, '']);

  const handleUploadDocuments = async (e) => {
    e.preventDefault();
    if (!selectedClubId) {
      toast({ title: 'No users selected', status: 'error' });
      return;
    }
    const formData = new FormData();
    for (let file of uploadFiles) formData.append('files', file);
    uploadUrls.forEach(url => url && formData.append('urls', url));
    formData.append('notes', uploadNotes);
    try {
      await fetch(`/api/documents/club/documents/upload/${selectedClubId}`, {
        method: 'POST',
        body: formData
      });
      toast({ title: 'Documents uploaded successfully!', status: 'success' });
      setUploadFiles([]);
      setUploadUrls(['']);
      setUploadNotes('');
      fetchClubDocuments(selectedClubId); // Refresh docs
      fetchClubsList(); // Refresh doc counts
    } catch (err) {
      toast({ title: 'Upload failed', description: err.message, status: 'error' });
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!selectedClubId) return;
    try {
      // This API endpoint would need to be implemented in your backend
      // e.g., DELETE /api/documents/:clubId/documents/:docId
      const res = await fetch(`/api/documents/${selectedClubId}/documents/${documentId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete document');
      toast({ title: 'Document deleted successfully.', status: 'success' });
      fetchClubDocuments(selectedClubId); // Refresh docs
      fetchClubsList(); // Refresh doc counts
    } catch (error) {
      toast({ title: 'Error deleting document.', description: error.message, status: 'error' });
    }
  };

  const handleDeleteKnowledgeBaseEntry = (entryId) => {
    setKnowledgeBase(prev => prev.filter(entry => entry.id !== entryId));
    toast({ title: 'Knowledge base entry deleted.', status: 'success' });
  };

  return (
    <PageLayout>
      <Box p={4}>
        <VStack spacing={6} align="stretch">
          <Heading size="lg">Documents Management</Heading>
          {/* Search Bar */}
          <Box mb={4}>
            <Input
              placeholder="Search users by name..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              size="md"
              bg="gray.800"
              color="white"
              borderColor="gray.600"
              _placeholder={{ color: 'gray.400' }}
            />
          </Box>
          {/* Club List Section */}
          <Box>
             <Heading size="md" mb={4}>Select a users to Manage Documents</Heading>
             {loadingClubs ? (
               <Spinner size="lg" />
             ) : (
               <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                 {filteredClubs.map(club => (
                   <Card
                     key={club.id}
                     p={4}
                     borderWidth="1px"
                     borderRadius="lg"
                     shadow="sm"
                     bg={selectedClubId === club.id ? 'purple.600' : 'gray.700'}
                     color={selectedClubId === club.id ? 'white' : 'gray.200'}
                     borderColor={selectedClubId === club.id ? 'purple.700' : 'gray.600'}
                     _hover={{
                       shadow: 'md',
                       transform: 'translateY(-2px)',
                       bg: selectedClubId === club.id ? 'purple.700' : 'gray.600',
                     }}
                     transition="all 0.2s"
                     cursor="pointer"
                     onClick={() => handleClubSelect(club.id)}
                   >
                     <HStack spacing={3} alignItems="center">
                        <Icon as={Building2} boxSize={6} />
                        <VStack align="start" spacing={0}>
                            <Text fontWeight="bold" fontSize="lg">{club.name}</Text>
                            <Text fontSize="sm">Total Documents: {club.documentCount}</Text>
                        </VStack>
                        <Spacer />
                        <Button
                          size="sm"
                          colorScheme="blue"
                          variant="outline"
                          onClick={e => { e.stopPropagation(); handleExportClubDocs(club.id); }}
                        >
                          Export Docs
                        </Button>
                     </HStack>
                   </Card>
                 ))}
               </SimpleGrid>
             )}
           </Box>

          {/* Conditional Sections for selectedClubId */}
          {selectedClubId && (
            <Tabs variant="enclosed" mt={6} colorScheme="purple">
              <TabList>
                <Tab>Knowledge Base</Tab>
                <Tab>users Documents</Tab>
              </TabList>
              <TabPanels>
                {/* Knowledge Base Tab */}
                <TabPanel p={4} bg="gray.700" borderWidth="1px" borderRadius="md">
                  <Heading size="md" mb={4}>Knowledge Base for {selectedClub?.name}</Heading>
                  <Box borderWidth="1px" borderRadius="md" p={4} mb={6} bg="gray.800">
                    <Heading size="sm" mb={4}>Existing Knowledge Base Entries</Heading>
                    {loadingKB ? (
                      <Spinner size="lg" />
                    ) : knowledgeBase.length === 0 ? (
                      <Text>No knowledge base entries yet.</Text>
                    ) : (
                      <VStack align="stretch" spacing={3}>
                        {knowledgeBase.map(entry => (
                          <Card key={entry.id} p={3} borderWidth="1px" borderRadius="md" bg="gray.600" color="gray.100">
                            <CardBody>
                              <HStack justifyContent="space-between">
                                {entry.type === 'text' ? (
                                  <VStack align="stretch">
                                    <Text fontWeight="bold">Source: {entry.source}</Text>
                                    <Text>{entry.text}</Text>
                                  </VStack>
                                ) : (
                                  <HStack>
                                    <Icon as={FiFileText} boxSize={5} />
                                    <VStack align="start" spacing={0}>
                                      <Text fontWeight="bold">File: {entry.fileName}</Text>
                                      <Text fontSize="sm" color="gray.400">Source: {entry.source}</Text>
                                    </VStack>
                                  </HStack>
                                )}
                                <IconButton
                                  aria-label="Delete Entry"
                                  icon={<FiTrash2 />}
                                  colorScheme="red"
                                  size="sm"
                                  onClick={() => handleDeleteKnowledgeBaseEntry(entry.id)}
                                />
                              </HStack>
                            </CardBody>
                          </Card>
                        ))}
                      </VStack>
                    )}
                  </Box>
                </TabPanel>

                {/* Club Documents Tab */}
                <TabPanel p={4} bg="gray.700" borderWidth="1px" borderRadius="md">
                  <Heading size="md" mb={4}>Documents for {selectedClub?.name}</Heading>
                  {/* Upload More Documents (Club Docs) */}
                  <Box mb={6} p={4} borderWidth="1px" borderRadius="md" bg="gray.800">
                    <Heading size="sm" mb={4}>Upload More Documents</Heading>
                    <form onSubmit={handleUploadDocuments}>
                      <VStack spacing={3} align="stretch">
                        <FormControl mb={3}>
                          <FormLabel htmlFor="upload-club-files">Choose Files (PDF, Word)</FormLabel>
                          <Input id="upload-club-files" type="file" multiple accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={handleUploadFileChange} bg="gray.700" borderColor="gray.600" color="white" />
                        </FormControl>
                        {uploadUrls.map((url, i) => (
                          <FormControl key={i} mb={3}>
                            <FormLabel>Document URL {i + 1}</FormLabel>
                            <Input value={url} onChange={e => handleUploadUrlChange(i, e.target.value)} placeholder="e.g., https://example.com/document.pdf" bg="gray.700" borderColor="gray.600" color="white" />
                          </FormControl>
                        ))}
                        <Button onClick={addUploadUrlField} type="button" colorScheme="teal" variant="outline" leftIcon={<FiUploadCloud />}>Add another URL Field</Button>
                        <FormControl mt={3}>
                          <FormLabel>Notes for Uploaded Documents</FormLabel>
                          <Textarea value={uploadNotes} onChange={e => setUploadNotes(e.target.value)} placeholder="Add any relevant notes for these documents" bg="gray.700" borderColor="gray.600" color="white" />
                        </FormControl>
                        <Button type="submit" colorScheme="teal" leftIcon={<FiUploadCloud />} mt={4}>Upload Documents</Button>
                      </VStack>
                    </form>
                  </Box>
                  {/* Existing Club Documents Section */}
                  <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.800">
                    <Heading size="sm" mb={4}>Existing users Documents</Heading>
                    {loadingDocs ? (
                      <Spinner size="lg" />
                    ) : clubDocuments.length === 0 ? (
                      <Text>No documents uploaded for this users yet.</Text>
                    ) : (
                      <VStack align="stretch" spacing={3}>
                        {clubDocuments.map(doc => (
                          <Card key={doc._id} p={3} borderWidth="1px" borderRadius="md" bg="gray.600" color="gray.100">
                            <CardBody>
                              <HStack justifyContent="space-between">
                                <VStack align="start" spacing={0}>
                                  <Text fontWeight="bold">{doc.originalName || (doc.urls && doc.urls.join(', ')) || 'Document'}</Text>
                                  {doc.notes && <Text fontSize="sm" color="gray.300">Notes: {doc.notes}</Text>}
                                  {doc.urls && doc.urls.length > 0 && (
                                    <Text fontSize="sm" color="gray.300">URLs: {doc.urls.join(', ')}</Text>
                                  )}
                                </VStack>
                                <HStack spacing={2}>
                                  {doc.fileName && (
                                    <Button
                                      as="a"
                                      href={`/api/documents/${selectedClubId}/documents/${doc._id}/download`}
                                      colorScheme="blue"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      leftIcon={<FiDownload />}
                                      size="sm"
                                    >
                                      Download
                                    </Button>
                                  )}
                                  <IconButton
                                    aria-label="Delete Document"
                                    icon={<FiTrash2 />}
                                    colorScheme="red"
                                    size="sm"
                                    onClick={() => handleDeleteDocument(doc._id)}
                                  />
                                </HStack>
                              </HStack>
                            </CardBody>
                          </Card>
                        ))}
                      </VStack>
                    )}
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </VStack>
      </Box>
    </PageLayout>
  );
};

export default DocumentsManagement; 